/**
 * vue-select hard-codes `role="combobox"` on the outer `.vs__dropdown-toggle` wrapper
 * while keyboard focus stays on the inner `.vs__search` input. Screen readers then don't
 * announce the focused input as a combobox and its accessible name is lost
 * (Deque: custom-combobox / WCAG 4.1.2). This mirrors what CustomSelect does internally:
 * demote the wrapper to presentation and expose a single combobox on the input.
 *
 * Use for raw `<VSelect>` usages that don't go through CustomSelect. Call it on
 * mount/open/close/search (after `nextTick`) so it re-asserts over vue-select's renders.
 *
 * @param {HTMLElement} rootEl - the VSelect root element (e.g. `vSelectRef.value.$el`).
 * @param {{ inputId?: string, ariaLabel?: string, isOpen?: boolean, describedById?: string }} [options]
 */
export function syncVueSelectComboboxRole(rootEl, options = {}) {
  if (!rootEl?.querySelector) return
  const {
    inputId = "",
    ariaLabel = "",
    isOpen = false,
    describedById = ""
  } = options

  const wrapper =
    rootEl.querySelector('[id$="__combobox"]') ||
    rootEl.querySelector(".vs__dropdown-toggle")
  const searchInput = rootEl.querySelector("input.vs__search")
  if (!searchInput) return

  const listboxId =
    wrapper?.getAttribute?.("aria-owns") ||
    searchInput.getAttribute("aria-controls") ||
    ""
  const open =
    isOpen ||
    rootEl.classList?.contains?.("vs--open") ||
    !!rootEl.querySelector?.(".vs--open")

  if (wrapper) {
    wrapper.setAttribute("role", "presentation")
    wrapper.removeAttribute("aria-expanded")
    wrapper.removeAttribute("aria-owns")
    wrapper.removeAttribute("aria-label")
    wrapper.removeAttribute("aria-describedby")
  }

  if (inputId) searchInput.setAttribute("id", inputId)
  // vue-select renders the input as `type="search"`, but `role="combobox"` is
  // only a valid override on `<input type="text">` per ARIA-in-HTML. Some screen
  // readers / validators ignore the combobox role on a search input (so the role
  // isn't announced). Normalize to text to match the ARIA APG combobox pattern.
  if (searchInput.getAttribute("type") !== "text") {
    searchInput.setAttribute("type", "text")
  }
  searchInput.setAttribute("role", "combobox")
  searchInput.setAttribute("aria-expanded", open ? "true" : "false")
  searchInput.setAttribute("aria-haspopup", "listbox")
  searchInput.setAttribute("aria-autocomplete", "list")
  if (ariaLabel) {
    searchInput.setAttribute("aria-label", ariaLabel)
    searchInput.removeAttribute("aria-labelledby")
  }
  if (listboxId && open) {
    searchInput.setAttribute("aria-controls", listboxId)
  } else {
    searchInput.removeAttribute("aria-controls")
  }
  if (describedById) {
    searchInput.setAttribute("aria-describedby", describedById)
  } else {
    searchInput.removeAttribute("aria-describedby")
  }
}

/**
 * vue-select keeps a placeholder <ul role="listbox"> in the DOM when closed.
 * Deque: hide from all users with display:none (not aria-hidden alone).
 */
export function syncVueSelectListboxAriaHidden() {
  if (typeof document === "undefined" || typeof window === "undefined") return

  document.querySelectorAll('ul[role="listbox"]').forEach((ul) => {
    if (!/^vs\d+__listbox$/.test(ul.id)) return
    const style = window.getComputedStyle(ul)
    const isHidden = style.display === "none" || style.visibility === "hidden"

    if (isHidden) {
      ul.style.display = "none"
      ul.setAttribute("hidden", "")
      ul.removeAttribute("aria-hidden")
    } else {
      ul.style.removeProperty("display")
      ul.removeAttribute("hidden")
      ul.removeAttribute("aria-hidden")
    }
  })
}
